function compact(values) {
  return values.filter((value) => value !== undefined && value !== null && value !== '')
}

function unique(values) {
  return [...new Set(compact(values))]
}

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`
}

function requesterName(group) {
  return group?.created_by_admin?.display_name
    || group?.created_by_admin?.email
    || 'Requester unavailable'
}

function destinations(group) {
  return Array.isArray(group?.destinations) ? group.destinations : []
}

function groupLabel(group) {
  return group?.document?.shared?.label || 'Untitled distributed post'
}

function organizationNames(group) {
  return unique(destinations(group).map((destination) => (
    destination.organization_name || destination.organization_slug
  )))
}

function marketSummary(group) {
  const market = group?.document?.shared?.market || {}
  const selectionCount = unique([
    ...(market.kambi_outcome_ids || []),
    ...(market.provider_selection_ids || []),
  ]).length
  const wagerCount = Array.isArray(market.wager_entries) ? market.wager_entries.length : 0
  const total = selectionCount + wagerCount
  return total ? pluralize(total, 'attached selection') : 'Content-only'
}

function destinationSummaries(group) {
  return destinations(group).map((destination) => {
    const organization = destination.organization_name || destination.organization_slug
    const scope = compact([
      pluralize(destination.surface_ids?.length || 0, 'surface'),
      pluralize(destination.regulation_ids?.length || 0, 'regulation'),
    ]).join(' · ')
    const overrides = destination.override_fields?.length
      ? ` · Overrides: ${destination.override_fields.join(', ')}`
      : ''
    return `${organization}: ${scope}${overrides}`
  })
}

export function getApprovalReadiness(group, detail) {
  if (!detail) {
    return { ready: false, reason: 'Approval details have not loaded.' }
  }
  if (group?.group_id && detail.group_id !== group.group_id) {
    return { ready: false, reason: 'The loaded details do not match the selected approval.' }
  }
  if (detail.approval_status !== 'pending_approval') {
    return { ready: false, reason: 'This distributed post is no longer pending approval.' }
  }
  if (!Number.isInteger(detail.revision) || detail.revision < 1) {
    return { ready: false, reason: 'The distributed post revision is unavailable.' }
  }

  const detailDestinations = destinations(detail)
  if (!detailDestinations.length) {
    return { ready: false, reason: 'This distributed post has no destinations to review.' }
  }
  const expectedCount = Math.max(
    destinations(group).length,
    Object.keys(detail?.document?.books || {}).length,
  )
  if (expectedCount > 0 && detailDestinations.length !== expectedCount) {
    return {
      ready: false,
      reason: `Only ${detailDestinations.length} of ${expectedCount} destinations loaded.`,
    }
  }
  if (detailDestinations.some((destination) => (
    !destination.post_id
    || !destination.organization_id
    || !(destination.organization_name || destination.organization_slug)
  ))) {
    return { ready: false, reason: 'One or more destinations are missing organization details.' }
  }
  if (detailDestinations.some((destination) => destination.approval_status !== 'pending_approval')) {
    return { ready: false, reason: 'One or more destinations are no longer pending approval.' }
  }
  if (detailDestinations.some((destination) => (
    destination.capable !== true || (destination.conflicts || []).length > 0
  ))) {
    return { ready: false, reason: 'One or more destinations failed capability review.' }
  }

  return { ready: true, reason: null }
}

export function buildPostGroupDecision(group, detail) {
  const resolved = detail || group
  const destinationCount = destinations(resolved).length
  const organizations = organizationNames(resolved)
  const destinationSummary = destinationSummaries(resolved)

  return {
    id: resolved?.group_id,
    label: groupLabel(resolved),
    postCount: destinationCount,
    readiness: getApprovalReadiness(group, detail),
    context: [
      { key: 'requester', label: 'Requested by', value: requesterName(resolved) },
      { key: 'organizations', label: 'Organizations', value: organizations.join(', ') || 'Organizations unavailable' },
      { key: 'creator', label: 'Creator', value: resolved?.creator?.display_name || 'Not assigned' },
      { key: 'market', label: 'Market / price', value: marketSummary(resolved) },
      { key: 'destinations', label: 'Destinations', value: destinationSummary.join('; ') || 'Destinations unavailable' },
    ],
    changes: [
      {
        key: 'approval',
        label: 'Approval',
        current: 'Pending approval',
        proposed: 'Approved',
        impact: `Changes all ${pluralize(destinationCount, 'member post')} together.`,
      },
      {
        key: 'publishing',
        label: 'Publishing',
        current: 'Blocked',
        proposed: 'Eligible',
        impact: 'Requests feed updates for the configured destinations.',
      },
    ],
    approvalConsequence: `Approving changes all ${pluralize(destinationCount, 'member post')} to Approved. Feed updates continue asynchronously for their configured destinations.`,
    rejectionConsequence: `Rejecting changes all ${pluralize(destinationCount, 'member post')} to Rejected and keeps them from publishing.`,
  }
}
